import { useMemo } from 'react';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useAuthStore } from '@/state/store/auth';
import { Employee, EmployeesPagingResult, useGetMyEmployee } from '@/modules/hr-directory';
import { AttendancePolicy, attendancePolicyOf } from '../services/attendance-rules';
import { AttendanceRuleContext } from '../services/attendance.service';

const { ATTENDANCE, PROFILE } = HRM_PERMISSIONS;

/**
 * Which attendance features and rules bind the signed-in user, from the permissions they hold (never
 * their country): the feature permissions below, and every rule permission of the catalog
 * (`@/constant/hr/attendance-rule-catalog`), where a group's highest-priority held rule wins.
 * `flexible` comes from the HR term (see `useAttendanceRuleContext`).
 */
export const useAttendancePolicy = (flexible = false): AttendancePolicy => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const permissions = useAuthStore((state) => state.user?.permissions) as string[] | undefined;

  return useMemo(() => {
    const held = new Set(isAuthenticated ? (permissions ?? []) : []);
    return attendancePolicyOf(
      {
        canClock: held.has(ATTENDANCE.CLOCK),
        canTakeBreak: held.has(ATTENDANCE.TAKE_BREAK),
        multipleSessions: held.has(ATTENDANCE.MULTIPLE_SESSIONS),
        overtime: held.has(PROFILE.OVERTIME_ENABLED),
        flexible,
      },
      (permission) => held.has(permission)
    );
  }, [isAuthenticated, permissions, flexible]);
};

/** The policy plus the employee facts the rules read: flexible hours (HR term), work from home
 * (the default when never set) and office days. */
export const useAttendanceRuleContext = (): AttendanceRuleContext => {
  const { data: myEmployeeData, isLoading } = useGetMyEmployee();
  const employee: Employee | undefined = (myEmployeeData as { Employees?: EmployeesPagingResult })
    ?.Employees?.items?.[0];
  const policy = useAttendancePolicy(Boolean(employee?.IsFlexible));

  return useMemo(
    () => ({
      policy,
      // Until the employee is loaded their office days are unknown: nothing is offered yet.
      isLoading,
      // Never set means working from home: the directory saves IsWFH false only with office days.
      // (Legacy's bool defaulted to office work; a profile without office days is in the office
      // every day, see `canSelfClockInToday`.)
      isWFH: employee?.IsWFH !== false,
      wfoDays: employee?.WFODays,
    }),
    [policy, isLoading, employee?.IsWFH, employee?.WFODays]
  );
};
